/**
 * Phase 51.1 · Client-side media performance telemetry.
 * ---------------------------------------------------------------
 * Samples real image/video load timings via PerformanceObserver and
 * ships them (batched, anonymous, fire-and-forget) to
 * POST /api/telemetry/media. Powers the Admin "Performance Media"
 * dashboard with REAL user-experienced numbers:
 *   - thumbs vs originals actually downloaded by browsers
 *   - avg load ms and avg KB per class
 *   - video autoplay / loaded / tap-hint events (FeedVideo)
 *
 * Never throws — telemetry must never break the app.
 */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let counters = {};
let initialized = false;
// Phase 51.2 · Which page is currently active ('feed' | 'explore' | null).
// Lets the dashboard compute the average media weight per Feed/Explore view.
let currentPage = null;

const bump = (key, val = 1) => {
  counters[key] = (counters[key] || 0) + val;
};

const classify = (name) => {
  if (!name || !name.includes('/api/media/')) return null;
  if (/\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(name)) return 'video';
  if (/[?&]w=\d+/.test(name)) return 'thumb';
  return 'original';
};

const onEntries = (list) => {
  try {
    for (const e of list.getEntries()) {
      const kind = classify(e.name);
      if (!kind) continue;
      bump(`${kind}_count`);
      bump(`${kind}_ms`, Math.max(0, e.duration || 0));
      bump(`${kind}_bytes`, Math.max(0, e.transferSize || 0));
      if (currentPage) {
        // Phase 51.2 · attribute media bytes to the active page for the
        // "average feed/explore weight" traffic lights.
        bump(`${currentPage}_page_bytes`, Math.max(0, e.transferSize || 0));
      }
    }
  } catch (_) { /* noop */ }
};

const flush = () => {
  if (!Object.keys(counters).length) return;
  const payload = JSON.stringify({ counters });
  counters = {};
  try {
    const url = `${BACKEND_URL}/api/telemetry/media`;
    const blob = new Blob([payload], { type: 'application/json' });
    const ok = navigator.sendBeacon && navigator.sendBeacon(url, blob);
    if (!ok) {
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    }
  } catch (_) { /* noop */ }
};

/** Record a discrete media event, e.g. recordMediaEvent('video_autoplay'). */
export const recordMediaEvent = (key, val = 1) => bump(key, val);

/** Phase 51.2 · Mark the active page for per-page weight attribution.
 * Call with 'feed'/'explore' on mount and null on unmount. */
export const setTelemetryPage = (page) => { currentPage = page || null; };

/** One-time init — call from App root. */
export const initMediaTelemetry = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  try {
    const obs = new PerformanceObserver(onEntries);
    obs.observe({ type: 'resource', buffered: true });
  } catch (_) { /* PerformanceObserver unsupported — events still flow */ }
  setInterval(flush, 20000);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
};
